import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { AddStepsDialogTabs, DEFAULT_DATA_PAGE_SIZE } from '@Pages/RetroSynthesis/EditPlan/AddSteps/constants';
import isEmpty from 'lodash/isEmpty';

import {
  cleanState,
  fetchExamplesData,
  fetchPredictedSteps,
  fetchPublishedSteps,
  fetchInHouseSteps,
  hideAddStepsDialog,
  setSelectedItemsByTab,
  addRouteSteps,
  fetchInHouseByDatabase,
} from '@Pages/RetroSynthesis/EditPlan/AddSteps/actions';
import { useHistory, useParams } from 'react-router-dom';
import {
  addStepsState,
  predictedResponse,
  selectedItemsByTab,
  inHouseDatabases,
  isInHouseLoadingByDatabase,
  inHouseResponseByDatabase,
  publishedResponse,
} from '@Pages/RetroSynthesis/EditPlan/AddSteps/selectors';
import { reaxysDatabaseId } from '@Services/UserDatabases/UserDatabasesSelectors';
import { openWith3d } from '@Containers/StructureDialog/StructureDialogAction';
import { RsRoute } from '@Pages/RetroSynthesis/routes';
import { fetchData } from '@Pages/RetroSynthesis/ProjectRoute/actions';
import { forceReload } from '@Pages/RetroSynthesis/components/RsSlidablePaginated/actions';
// Feature switch for In-House
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { UrlParams } from '@Pages/RetroSynthesis/constants';
import get from 'lodash/get';
import AddStepsDialog from '@Pages/RetroSynthesis/EditPlan/AddSteps/AddStepsDialog';
import { getProjectsInfoMap } from '@Pages/RetroSynthesis/RsBreadcrumbs/selector';
import { getPredictivePartner, isPredictiveRetrosynthesisEnabled } from '../../predictiveUtils';
/* eslint-disable react/prop-types */
/* eslint-disable no-shadow */
/* eslint-disable react-hooks/exhaustive-deps */
function AddStepsDialogContainer({
  isPredictedEnabled,
  isLoading,
  selectedStep,
  hideAddStepsDialog,
  fetchPublishedSteps,
  fetchPredictedSteps,
  cleanState,
  openStructure,
  publishedResponse,
  isPublishedLoading,
  predictedResponse,
  isPredictedLoading,
  selectedStepExamplesSearchData,
  fetchExamplesData,
  projectsInfo,

  // Selection
  selectedItemsByTab,
  setSelectedItemsByTab,

  addRouteSteps,
  newRoute,
  fetchRouteData,
  forceReloadThumbnails,

  // In-house
  reaxysDatabaseId,
  inHouseDatabases,
  fetchInHouseSteps,
  fetchInHouseByDatabase,
  isInHouseLoadingByDatabase,
  inHouseResponseByDatabase,
  ...rest
}) {
  const { projectId, routeId } = useParams();
  const history = useHistory();
  const projectInfo = projectsInfo && projectsInfo[projectId];
  const onAddStep = () => {
    let newSteps = [];
    // 1 published
    // eslint-disable-next-line no-use-before-define
    const newPublishedSteps = _prepareNewPublishedSteps(AddStepsDialogTabs.PUBLISHED, reaxysDatabaseId);
    newSteps = newSteps.concat(newPublishedSteps);
    // 2 predicted
    // eslint-disable-next-line no-use-before-define
    const predicted = _prepareNewPredictedSteps();
    newSteps = newSteps.concat(predicted);
    // 3 inhouse
    // eslint-disable-next-line no-use-before-define
    const inhouse = Object.keys(inHouseResponseByDatabase).map((db) => _prepareNewPublishedSteps(db, db));
    newSteps = newSteps.concat(...inhouse);

    // call add action
    addRouteSteps(projectId, routeId, newSteps, {
      publishedCount: newPublishedSteps.length,
      predictedCount: predicted.length,
      inHouseCount: inhouse.length,
      projectInfo,
    });
  };

  useEffect(() => {
    if (newRoute) {
      // reloaded route
      if (newRoute.toString() !== routeId) {
        // new
        history.push(RsRoute.getProjectRouteTreeViewLink(projectId, newRoute));
      } else {
        // updated
        fetchRouteData({ projectId, routeId, cache: false });
      }
      forceReloadThumbnails(newRoute);
    }
  }, [newRoute]);

  const fetchPublishedData = React.useCallback(
    (payload = {}) => {
      const page = payload.size !== get(publishedResponse, 'size', DEFAULT_DATA_PAGE_SIZE) ? 0 : payload.page;
      const size = payload.size || get(publishedResponse, 'size', DEFAULT_DATA_PAGE_SIZE);

      fetchPublishedSteps(selectedStep, {
        [UrlParams.PAGE]: page,
        [UrlParams.SIZE]: size,
      });
    },
    [publishedResponse]
  );

  const fetchInhouseData = React.useCallback(
    (databaseId, payload = {}) => {
      const page = payload.size !== get(inHouseResponseByDatabase[databaseId], 'size', DEFAULT_DATA_PAGE_SIZE) ? 0 : payload.page;
      const size = payload.size || get(inHouseResponseByDatabase[databaseId], 'size', DEFAULT_DATA_PAGE_SIZE);

      fetchInHouseByDatabase(selectedStep, databaseId, {
        [UrlParams.PAGE]: page,
        [UrlParams.SIZE]: size,
      });
    },
    [inHouseResponseByDatabase]
  );

  const fetchPredictedData = React.useCallback(
    (payload = {}, firstLoad = false) => {
      const page = payload.size !== get(predictedResponse, 'size', DEFAULT_DATA_PAGE_SIZE) ? 0 : payload.page;
      const size = payload.size || get(predictedResponse, 'size', DEFAULT_DATA_PAGE_SIZE);

      const jobId = firstLoad ? undefined : get(predictedResponse, 'jobId', undefined); // reset predicted JobId on first page load and reuse on paging
      const partner = getPredictivePartner();
      fetchPredictedSteps(selectedStep, projectId, partner, {
        jobId,
        [UrlParams.PAGE]: page,
        [UrlParams.SIZE]: size,
      });
    },
    [predictedResponse]
  );

  useEffect(() => {
    if (selectedStep && selectedStep.substance) {
      fetchPublishedData();

      if (isPredictedEnabled) {
        fetchPredictedData({}, true);
      }

      // In-house
      if (!isEmpty(inHouseDatabases)) {
        fetchInHouseSteps(
          selectedStep,
          inHouseDatabases.map((db) => db.id)
        );
      }
    }
  }, [selectedStep]);

  useEffect(() => {
    if (!isEmpty(predictedResponse) && !isEmpty(predictedResponse.content) && selectedStep.substance) {
      fetchExamplesData(selectedStep, predictedResponse.content);
    }
  }, [predictedResponse]);

  useEffect(() => {
    return () => {
      // hide dialog on unmount
      hideAddStepsDialog();
    };
  }, []);

  const onClose = () => {
    hideAddStepsDialog();
  };

  const _prepareNewPublishedSteps = (tab, databaseId) => {
    let layouts = [];

    if (selectedItemsByTab && selectedItemsByTab[tab] && selectedItemsByTab[tab].length) {
      layouts = selectedItemsByTab[tab].map((selectedLayout, i) => {
        const isMultiStep = selectedLayout?.details?.some((detail) => detail?.multiStepDetails && detail?.scheme);
        const productId = i + 1; // arbitrary number used only for referring to product from reactants
        const reactionId = selectedLayout.id;
        const product = {
          id: productId,
          reactionId,
          databaseId,
          substanceId: selectedLayout.products[0].id,
          structure: selectedLayout.products[0].molImage.structure,
          yield:
            selectedLayout.details[0].yields && selectedLayout.details[0].yields.length
              ? selectedLayout.details[0].yields[0].yieldValue
              : '',
        };
        const reactants = selectedLayout.reactants.map((reactant) => {
          return {
            parentId: productId,
            reactionId,
            databaseId,
            substanceId: reactant.id,
            structure: reactant.molImage.structure,
          };
        });
        return {
          type: 'PUBLISHED',
          parentId: selectedStep.id,
          reactants: [product, ...reactants],
          multiStep: isMultiStep,
        };
      });
    }
    return layouts;
  };

  const _prepareNewPredictedSteps = () => {
    let layouts = [];
    if (selectedItemsByTab && selectedItemsByTab[AddStepsDialogTabs.PREDICTED] && selectedItemsByTab[AddStepsDialogTabs.PREDICTED].length) {
      layouts = selectedItemsByTab[AddStepsDialogTabs.PREDICTED].map((selectedLayout) => {
        return {
          type: 'RETROSYNTHESIS',
          parentId: selectedStep.id,
          reactants: selectedLayout.routeLayout.map((substance) => {
            return {
              confidence: substance.confidence,
              examples: substance.examples,
              structure: substance.substance.structure,
              id: substance.id,
              parentId: substance.parentId,
              substanceId: substance.substance.subDetails ? substance.substance.subDetails.id : undefined,
              databaseId: substance.substance.databaseId,
            };
          }),
        };
      });
    }
    return layouts;
  };

  return (
    <AddStepsDialog
      isPredictedEnabled={isPredictedEnabled}
      isLoading={isLoading}
      selectedStep={selectedStep}
      onClose={onClose}
      onTargetStructureClick={(structure) => openStructure(null, structure)}
      onAddStep={onAddStep}
      published={publishedResponse}
      fetchPublishedData={fetchPublishedData}
      predicted={predictedResponse}
      isPredictedLoading={isPredictedLoading}
      isPublishedLoading={isPublishedLoading}
      fetchPredictedData={fetchPredictedData}
      selectedStepExamplesSearchData={selectedStepExamplesSearchData}
      selectedItemsByTab={selectedItemsByTab}
      setSelectedItemsByTab={setSelectedItemsByTab}
      reaxysDatabaseId={reaxysDatabaseId}
      inHouseDatabases={inHouseDatabases}
      fetchInhouseData={fetchInhouseData}
      isInHouseLoadingByDatabase={isInHouseLoadingByDatabase}
      inHouseResponseByDatabase={inHouseResponseByDatabase}
      {...rest}
    />
  );
}

function mapStateToProps(state) {
  const isInHouseEnabled = Auth.isFeatureEnabled(FEATURES.enableInHouseForEditableRetrosynthesisPlans);
  const { isLoading, isPredictedLoading, isPublishedLoading, error, selectedStep, selectedStepExamplesSearchData, newRoute } =
    addStepsState(state);
  return {
    isPredictedEnabled: isPredictiveRetrosynthesisEnabled(),
    isLoading,
    isPredictedLoading,
    isPublishedLoading,
    error,
    selectedStep,
    selectedStepExamplesSearchData,
    publishedResponse: publishedResponse(state),
    predictedResponse: predictedResponse(state),
    selectedItemsByTab: selectedItemsByTab(state),
    newRoute,
    projectsInfo: getProjectsInfoMap(state),

    // In-house
    reaxysDatabaseId: reaxysDatabaseId(state),
    inHouseDatabases: isInHouseEnabled ? inHouseDatabases(state) : [],
    isInHouseLoadingByDatabase: isInHouseLoadingByDatabase(state),
    inHouseResponseByDatabase: inHouseResponseByDatabase(state),
  };
}

const mapDispatchToProps = {
  fetchPublishedSteps,
  fetchPredictedSteps,
  fetchInHouseSteps,
  fetchInHouseByDatabase,
  fetchExamplesData,
  cleanState,
  hideAddStepsDialog,
  openStructure: openWith3d,
  setSelectedItemsByTab,
  addRouteSteps,
  fetchRouteData: fetchData,
  forceReloadThumbnails: forceReload,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddStepsDialogContainer);
